import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router"
import { ArrowLeft, ArrowUpRight, SearchMd } from "@untitledui/icons"
import { toast } from "sonner"

import type { GhlConversationRich } from "@/lib/api/ghl-types"
import { useDecide, useGhlConversationsRich, useGhlUsers, useMarkRead } from "@/lib/api/use-ghl"
import { cx } from "@/lib/utils/cx"
import { BookDialog } from "./book-dialog"
import { FOLLOW_UP } from "./follow-up"
import { OfferDialog } from "./offer-dialog"
import { ThreadPanel } from "./thread-panel"
import { Avatar, ChannelIcon, Empty, INPUT, LIGHT_BTN, fmtDateTime, pill, relTime, userName } from "./shared"

// Conversations — every thread on the shop's number and email. Waiting on
// you first; the same segments as the inbox; one-tap replies; book from
// the thread; an offer to the people in the list.

type Seg = "waiting" | "following" | "closed" | "opted_out" | "all"
const SEGS: Array<{ key: Seg; label: string }> = [
  { key: "waiting", label: "Waiting on you" }, { key: "following", label: "Following up" }, { key: "closed", label: "Booked & won" }, { key: "opted_out", label: "Opted out" }, { key: "all", label: "All" },
]
const inSeg = (r: GhlConversationRich, s: Seg) =>
  s === "all" ? true : s === "waiting" ? r.unreadCount > 0 : s === "closed" ? r.followUp === "booked" || r.followUp === "won" : s === "opted_out" ? r.dnd : (r.followUp === "following" || r.followUp === "to_call" || r.followUp === "to_confirm") && r.unreadCount === 0

export function Conversations({ teamId }: { teamId: number | string }) {
  const [seg, setSeg] = useState<Seg>("waiting")
  const [q, setQ] = useState("")
  const [openId, setOpenId] = useState<string | null>(null)
  const [offer, setOffer] = useState(false)
  const { data: all = [], isLoading } = useGhlConversationsRich(teamId, {})
  const { data: users = [] } = useGhlUsers(teamId)

  const counts = useMemo(() => Object.fromEntries(SEGS.map((s) => [s.key, all.filter((r) => inSeg(r, s.key)).length])) as Record<Seg, number>, [all])
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return all.filter((r) => inSeg(r, seg)).filter((r) => !needle || `${r.contactName} ${r.vehicle ?? ""} ${r.lastMessageBody}`.toLowerCase().includes(needle))
  }, [all, seg, q])
  useEffect(() => { if (!openId && rows[0] && window.innerWidth >= 1024) setOpenId(rows[0].id) }, [rows, openId])
  const open = all.find((r) => r.id === openId) ?? null

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[400px_minmax(0,1fr)]">
      <div className={cx("min-w-0", open && "hidden lg:block")}>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Show">
          {SEGS.map((s) => (
            <button key={s.key} type="button" role="radio" aria-checked={seg === s.key} onClick={() => setSeg(s.key)} className={cx(pill(seg === s.key), "h-9 px-3")}>
              {s.label} <span className={cx("ml-1 tabular-nums", seg === s.key ? "text-white/70" : "text-zinc-400")}>{counts[s.key]}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <SearchMd className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search conversations" className={cx(INPUT, "w-full pl-9")} />
          </div>
          <button type="button" onClick={() => setOffer(true)} disabled={rows.length === 0} className={cx(LIGHT_BTN, "shrink-0")}>Send an offer</button>
        </div>
        <ul className="mt-3 divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white">
          {isLoading && all.length === 0 && <li><Empty>Loading…</Empty></li>}
          {!isLoading && rows.length === 0 && <li><Empty>{seg === "waiting" ? "Nobody is waiting on you." : "Nothing here."}</Empty></li>}
          {rows.map((r) => <RowItem key={r.id} r={r} on={r.id === openId} onOpen={() => setOpenId(r.id)} />)}
        </ul>
      </div>

      <div className={cx("min-w-0", !open && "hidden lg:block")}>
        {open ? (
          <Thread key={open.id} teamId={teamId} conv={open} owner={userName(users, open.assignedTo)} users={users} onBack={() => setOpenId(null)} />
        ) : (
          <div className="rounded-xl border border-dashed border-zinc-300 px-6 py-24 text-center text-[13.5px] text-zinc-500">Pick a conversation.</div>
        )}
      </div>

      <OfferDialog open={offer} teamId={teamId} recipients={rows.map((r) => ({ id: r.contactId, firstName: r.contactName.split(" ")[0], phone: r.contactPhone, smsBlocked: !r.contactPhone || r.dnd }))} onClose={() => setOffer(false)} />
    </div>
  )
}

function RowItem({ r, on, onOpen }: { r: GhlConversationRich; on: boolean; onOpen: () => void }) {
  const fu = r.followUp ? FOLLOW_UP[r.followUp] : null
  const meta = r.unreadCount > 0 ? null : r.bookedAt ? `Booked ${fmtDateTime(r.bookedAt)}` : r.nextTouchAt ? `Next: ${r.nextTouchTitle ?? "follow-up"} · ${relTime(r.nextTouchAt)}` : null
  return (
    <li>
      <button type="button" onClick={onOpen} aria-current={on} className={cx("flex w-full items-start gap-3 border-l-2 px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/30", on ? "border-zinc-900 bg-zinc-50" : "border-transparent hover:bg-zinc-50/70")}>
        <Avatar name={r.contactName} />
        <span className="min-w-0 flex-1 overflow-hidden">
          <span className="flex items-baseline justify-between gap-2">
            <span className={cx("truncate text-[14px]", r.unreadCount > 0 ? "font-semibold text-zinc-900" : "font-medium text-zinc-800")}>{r.contactName}</span>
            <span className="shrink-0 text-[11.5px] text-zinc-400">{relTime(r.lastMessageDate)}</span>
          </span>
          <span className="block truncate text-[12px] text-zinc-500">{[r.vehicle, r.dealValue != null ? `$${Math.round(r.dealValue).toLocaleString()}` : null].filter(Boolean).join(" · ")}</span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <ChannelIcon type={r.lastMessageType} className="size-3.5 shrink-0 text-zinc-400" />
            <span className={cx("truncate text-[13px]", r.unreadCount > 0 ? "text-zinc-900" : "text-zinc-500")}>{r.lastMessageDirection === "outbound" ? "You: " : ""}{r.lastMessageBody}</span>
            {r.unreadCount > 0 && <span className="ml-auto size-2 shrink-0 rounded-full bg-zinc-900" aria-label="Unread" />}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px]">
            {fu && <span className={cx("inline-flex items-center gap-1 font-medium", fu.text)}><span className={cx("size-1.5 rounded-full", fu.dot)} aria-hidden />{fu.label}</span>}
            {meta && <span className="text-zinc-500">{meta}</span>}
            {r.dnd && <span className="font-bold uppercase tracking-[0.06em] text-red-700">Opted out</span>}
          </span>
        </span>
      </button>
    </li>
  )
}

function Thread({ teamId, conv, owner, onBack }: { teamId: number | string; conv: GhlConversationRich; owner: string; users: ReturnType<typeof useGhlUsers>["data"]; onBack: () => void }) {
  const decide = useDecide(teamId)
  const markRead = useMarkRead(teamId)
  const [booking, setBooking] = useState(false)
  const first = conv.contactName.split(" ")[0]
  const book = async (startsAt: string, kind: "inspection" | "dropoff") => {
    try { const r = await decide.mutateAsync({ kind: "book", contactId: conv.contactId, startsAt, appointmentKind: kind }); toast.success(r.note ?? "Booked"); setBooking(false) } catch { toast.error("Couldn't book — try again") }
  }
  return (
    <div className="flex h-[calc(100dvh-14rem)] min-h-[520px] flex-col">
      <div className="flex items-center gap-3 rounded-t-xl border border-b-0 border-zinc-200 bg-white px-4 py-3">
        <button type="button" onClick={onBack} className="grid size-9 place-items-center rounded-full text-zinc-500 hover:bg-zinc-100 lg:hidden" aria-label="Back to the list"><ArrowLeft className="size-4" /></button>
        <Avatar name={conv.contactName} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14.5px] font-semibold text-zinc-900">{conv.contactName}</p>
          <p className="truncate text-[12.5px] text-zinc-500">{[conv.vehicle, conv.contactPhone, conv.stageName, owner !== "Unassigned" ? owner : null].filter(Boolean).join(" · ")}</p>
        </div>
        {conv.unreadCount > 0 && <button type="button" onClick={() => markRead.mutate(conv.id, { onSuccess: () => toast("Marked handled") })} className="h-9 rounded-md px-2 text-[13px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900">Handled</button>}
        <button type="button" onClick={() => setBooking(true)} className={cx(LIGHT_BTN, "h-9")}>Book {first}</button>
        <Link to={`/marketing/contacts/${conv.contactId}`} className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-[13px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900">Open <ArrowUpRight className="size-3.5" aria-hidden /></Link>
      </div>
      <ThreadPanel teamId={teamId} contactId={conv.contactId} conversationId={conv.id} firstName={first} phone={conv.contactPhone} email={conv.contactEmail} smsBlocked={conv.dnd} emailBlocked={conv.dnd} className="min-h-0 flex-1 rounded-t-none" />
      <BookDialog open={booking} teamId={teamId} contactName={conv.contactName} onClose={() => setBooking(false)} onBook={book} />
    </div>
  )
}
